import React from 'react';
import branch from 'recompose/branch';
import renderComponent from 'recompose/renderComponent';
import Message from '../Message';
import { EnhancedError, EVENTUI_ENHANCED_ERROR_TYPE } from '../EnhancedError';
import partition from 'lodash/partition';
import get from 'lodash/get';
import omit from 'lodash/omit';

/**
 * Graphql props function that exposes errors in `data.error.graphQLErrors`
 * associated with the given query as `data.warnings`, and removes the
 * `data.error` object unless there are other errors (graphql or network).
 * Use with `errorPolicy: 'all'` in graphql() options.
 *
 * Example:

    graphql(query, {
      options: props => ({
        errorPolicy: 'all',
        variables: {
          first: 25,
        },
      }),
      props: compose(
        queryErrorsToWarnings('searchColleges'),
        loadMorePaginationProp(query),
      ),
    })

 * @param queryName  the graphql query name e.g. 'searchColleges'
 * @returns {function}  the function to be used as 'props' function for graphql()
 */
export function queryErrorsToWarnings(queryName) {
  return (props) => {
    const { data, ...rest } = props;
    // eslint-disable-next-line
    if (get(data, ['error', 'graphQLErrors'])) {
      const {
        error: { graphQLErrors, networkError },
      } = data;
      const [queryErrors, otherErrors] = partition(
        graphQLErrors,
        // select errors associated with specific data nodes in the selected query - excludes general query errors
        (error) => error.path && error.path.length > 1 && error.path[0] === queryName,
      );
      if (queryErrors.length) {
        const dropError = !otherErrors.length && !networkError;
        return {
          ...rest,
          data: {
            ...(dropError ? omit(data, 'error') : data),
            warnings: queryErrors,
          },
        };
      }
    }
    return props;
  };
}

export default branch(
  ({ data }) => data && data.error,
  renderComponent(({ data }) => {
    const { graphQLErrors, networkError } = data.error;
    if (networkError) {
      return (
        <EnhancedError
          iconName="exclamation triangle"
          size="large"
          className="eventui-network-error"
          errorType={EVENTUI_ENHANCED_ERROR_TYPE.NETWORK}
          refetch={data.refetch}
        />
      );
    } else if (graphQLErrors && graphQLErrors.length > 0) {
      return (
        <EnhancedError
          iconName="exclamation triangle"
          size="large"
          className="eventui-graphql-error"
          errorType={EVENTUI_ENHANCED_ERROR_TYPE.GRAPHQL}
          error={graphQLErrors[0]}
        />
      );
    }
    return <Message error header="Oops, something went wrong" content={data.error.message} />;
  }),
);
